import { Box, Button, Card, CardContent, TextField } from '@mui/material';
import { UserLoginProps } from '@turiya/login-component/lib/screens';
import React, { MouseEvent, useEffect } from 'react';
import logo from '../../assets/favicon.png';
import { API_WORKFLOW_KEY, IdentityProvider } from '../../common/env';
import { getEnvironment, useDataState } from '../../common/state';

export const LOGIN_METHOD_LS_KEY = 'phoenix:login-type';
export const SSO_PROVIDER_LS_KEY = 'phoenix:sso-provider';
const USER_NAME_PASS_METHOD = 'user-name-pass';
const SSO_METHOD = 'sso';

export const LoginScreen: React.FC<UserLoginProps> = ({ loginWithIdentityProvider, loginWithWorkflow }) => {
    const [userName, setUserName] = React.useState('');
    const [password, setPassword] = React.useState('');
    const { enableSSO, enableSSOAutomaticLogin, identityProviders } = useDataState(getEnvironment);
    const existingLoginType = localStorage.getItem(LOGIN_METHOD_LS_KEY);
    const { appId } = useDataState(getEnvironment);

    const login = async (e: MouseEvent): Promise<void> => {
        e.preventDefault();
        e.stopPropagation();
        localStorage.setItem(LOGIN_METHOD_LS_KEY, USER_NAME_PASS_METHOD);
        loginWithWorkflow(appId, API_WORKFLOW_KEY, userName, {
            transient_userName: userName,
            transient_password: password,
        })
            .then(console.log)
            .catch(alert);
    };

    const loginWithIDP = async (e: MouseEvent, provider: IdentityProvider): Promise<void> => {
        e.preventDefault();
        e.stopPropagation();
        localStorage.setItem(LOGIN_METHOD_LS_KEY, SSO_METHOD);
        localStorage.setItem(SSO_PROVIDER_LS_KEY, provider.alias);
        loginWithIdentityProvider(provider.alias).then(console.log).catch(alert);
    };

    useEffect(() => {
        if (enableSSO && existingLoginType === SSO_METHOD && enableSSOAutomaticLogin) {
            // try to auto login with the provider the user last chose, else the default (first) one
            const lastUsedAlias = localStorage.getItem(SSO_PROVIDER_LS_KEY);
            const provider = identityProviders.find((each) => each.alias === lastUsedAlias) ?? identityProviders[0];
            if (provider) {
                loginWithIdentityProvider(provider.alias).then(console.log).catch(alert);
            }
        }
    }, [enableSSO, existingLoginType, loginWithIdentityProvider, enableSSOAutomaticLogin, identityProviders]);

    return (
        <Box
            sx={{
                display: 'flex',
                width: '100vw',
                height: '100vh',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'white',
            }}>
            <form>
                <Card
                    sx={{
                        display: 'flex',
                        maxWidth: '50em',
                        flexWrap: 'wrap',
                        borderRadius: '1em',
                        justifyContent: 'center',
                        backgroundColor: '#F0F2F7',
                    }}>
                    <CardContent style={{ minWidth: '28em', display: 'flex', justifyContent: 'center' }}>
                        <img style={{ marginTop: '2em', marginBottom: '1em', width: '3em' }} alt={'logo'} src={logo} />
                    </CardContent>
                    <CardContent style={{ minWidth: '28em', display: 'flex', justifyContent: 'center' }}>
                        <p style={{ marginBottom: '0.6em', color: '#2A3B75', fontWeight: 700, fontSize: '1.6rem' }}>
                            Log in
                        </p>
                    </CardContent>
                    <CardContent style={{ minWidth: '28em', display: 'flex', padding: 0 }}>
                        <p style={{ marginBottom: '0.1em', marginLeft: '0.2em', color: '#2A3B75', fontSize: '0.9rem' }}>
                            Enter Your Username
                        </p>
                    </CardContent>
                    <CardContent style={{ minWidth: '28em', display: 'flex', justifyContent: 'center' }}>
                        <TextField
                            name={'username'}
                            value={userName}
                            variant={'outlined'}
                            sx={{ width: '100%', backgroundColor: 'white' }}
                            onChange={(e): void => setUserName(e.currentTarget.value)}
                        />
                    </CardContent>
                    <CardContent style={{ minWidth: '28em', display: 'flex', padding: 0 }}>
                        <p style={{ marginBottom: '0.1em', marginLeft: '0.2em', color: '#2A3B75', fontSize: '0.9rem' }}>
                            Enter Your Password
                        </p>
                    </CardContent>
                    <CardContent style={{ minWidth: '28em', display: 'flex', justifyContent: 'center' }}>
                        <TextField
                            value={password}
                            name={'password'}
                            type={'password'}
                            variant={'outlined'}
                            sx={{ width: '100%', backgroundColor: 'white' }}
                            onChange={(e): void => setPassword(e.currentTarget.value)}
                        />
                    </CardContent>
                    <CardContent style={{ minWidth: '28em', display: 'flex', justifyContent: 'center' }}>
                        <Button
                            onClick={login}
                            type={'submit'}
                            variant={'contained'}
                            sx={{
                                width: '100%',
                                height: '4em',
                                borderRadius: '0.6em',
                                background: 'linear-gradient(92.76deg, #FF7C91 1.88%, #EB5A72 97.88%)',
                            }}>
                            Login
                        </Button>
                    </CardContent>
                    {enableSSO && identityProviders.length > 0 ? (
                        <>
                            <CardContent style={{ minWidth: '28em', display: 'flex', justifyContent: 'center' }}>
                                <p style={{ color: '#2A3B75', fontWeight: 700, fontSize: '1.6rem' }}>Or Sign In With</p>
                            </CardContent>
                            {identityProviders.map((provider) => (
                                <CardContent
                                    key={provider.alias}
                                    style={{
                                        minWidth: '28em',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        marginBottom: '1em',
                                    }}>
                                    <Button
                                        onClick={(e): Promise<void> => loginWithIDP(e, provider)}
                                        type={'submit'}
                                        variant={'contained'}
                                        sx={{
                                            width: '100%',
                                            height: '4em',
                                            borderRadius: '0.6em',
                                            background: 'linear-gradient(92.76deg, #FF7C91 1.88%, #EB5A72 97.88%)',
                                        }}>
                                        {provider.label}
                                    </Button>
                                </CardContent>
                            ))}
                        </>
                    ) : null}
                </Card>
            </form>
        </Box>
    );
};
